import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Input, message, Progress, Tag, Popconfirm, Empty, Typography } from 'antd';
import { PlusOutlined, CloudUploadOutlined, DeleteOutlined, FolderOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;

const OFFICE = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
const ACCEPT = '.ppt,.pptx,.doc,.docx,.xls,.xlsx,.pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.mp4,.mov,.txt,.csv,.zip';

const fileIcon = (t) => ({
  pdf: '📕', doc: '📘', docx: '📘', xls: '📗', xlsx: '📗', ppt: '📙', pptx: '📙',
  jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️', bmp: '🖼️', webp: '🖼️',
  mp4: '🎬', mov: '🎬', m4v: '🎬', zip: '🗜️', txt: '📄', csv: '📄',
}[(t || '').toLowerCase()] || '📄');

const fmtSize = (b) => {
  b = Number(b) || 0;
  if (b < 1024) return `${b}B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)}KB`;
  return `${(b / 1048576).toFixed(1)}MB`;
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const NetdiskModal = ({ open, onClose, isPremium, onChange }) => {
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const inputRef = useRef(null);

  const loadOverview = useCallback(async () => {
    try {
      const r = await axios.get('/netdisk/overview', { headers: authHeaders() });
      if (r.data.success) setOverview(r.data);
    } catch (_) { /* ignore */ }
  }, []);

  const loadFolders = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/netdisk/folders', { headers: authHeaders() });
      if (r.data.success) {
        setFolders(r.data.folders || []);
        setActiveFolder((cur) => cur || (r.data.folders && r.data.folders[0]?.id) || null);
      }
    } catch (e) {
      message.error(e.response?.data?.message || '获取目录失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFiles = useCallback(async (folderId) => {
    if (!folderId) { setFiles([]); return; }
    try {
      const r = await axios.get('/netdisk/files', { headers: authHeaders(), params: { folderId } });
      if (r.data.success) setFiles(r.data.files || []);
    } catch (_) { setFiles([]); }
  }, []);

  useEffect(() => {
    if (open && isPremium) { loadOverview(); loadFolders(); }
    if (!open) { setActiveFolder(null); setFiles([]); }
  }, [open, isPremium, loadOverview, loadFolders]);

  useEffect(() => { if (open && activeFolder) loadFiles(activeFolder); }, [open, activeFolder, loadFiles]);

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { message.warning('请输入目录名'); return; }
    try {
      const r = await axios.post('/netdisk/folders', { name }, { headers: authHeaders() });
      if (r.data.success) {
        message.success('目录已创建');
        setNewFolderOpen(false); setNewFolderName('');
        setActiveFolder(r.data.folder.id);
        loadFolders();
      }
    } catch (e) { message.error(e.response?.data?.message || '创建失败'); }
  };

  const deleteFolder = async (id) => {
    try {
      await axios.delete(`/netdisk/folders/${id}`, { headers: authHeaders() });
      message.success('目录已删除');
      if (activeFolder === id) setActiveFolder(null);
      loadFolders(); loadOverview(); onChange && onChange();
    } catch (e) { message.error(e.response?.data?.message || '删除失败'); }
  };

  const activeFolderName = (folders.find((f) => f.id === activeFolder) || {}).name || '';
  const inMiniProgram = typeof window !== 'undefined' && window.__wxjs_environment === 'miniprogram';

  const triggerUpload = () => {
    if (!activeFolder) { message.warning('请先选择或新建一个目录'); return; }
    // 小程序 web-view 里 <input type=file> 不可用，跳转到原生上传页（支持从聊天记录转存）
    if (inMiniProgram && window.wx && window.wx.miniProgram && typeof window.wx.miniProgram.navigateTo === 'function') {
      const token = localStorage.getItem('token') || '';
      const url = `/pages/netdisk-upload/netdisk-upload?token=${encodeURIComponent(token)}&folderId=${activeFolder}&folderName=${encodeURIComponent(activeFolderName)}`;
      window.wx.miniProgram.navigateTo({ url });
      return;
    }
    inputRef.current && inputRef.current.click();
  };

  // 从原生上传页返回后，网页重新获得焦点时刷新文件列表
  useEffect(() => {
    if (!open) return;
    const onVisible = () => { if (document.visibilityState === 'visible' && activeFolder) { loadFiles(activeFolder); loadOverview(); } };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [open, activeFolder, loadFiles, loadOverview]);

  const onFilePicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folderId', activeFolder);
    try {
      const r = await axios.post('/netdisk/upload', fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.success) {
        message.success('上传成功');
        loadFiles(activeFolder); loadOverview(); onChange && onChange();
      } else {
        message.error(r.data.message || '上传失败');
      }
    } catch (err) {
      message.error(err.response?.data?.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (id) => {
    try {
      await axios.delete(`/netdisk/files/${id}`, { headers: authHeaders() });
      loadFiles(activeFolder); loadOverview(); onChange && onChange();
    } catch (e) { message.error('删除失败'); }
  };

  const openFile = (f) => {
    const url = window.location.origin + f.url;
    if (OFFICE.includes((f.file_type || '').toLowerCase())) {
      window.open('https://view.officeapps.live.com/op/view.aspx?src=' + encodeURIComponent(url), '_blank');
    } else {
      window.open(url, '_blank');
    }
  };

  const usedMB = overview ? (overview.usedBytes / 1048576) : 0;
  const quotaMB = overview ? (overview.quotaBytes / 1048576) : 100;
  const pct = Math.min(100, Math.round((usedMB / quotaMB) * 100));

  return (
    <Modal title="📁 我的网盘" open={open} onCancel={onClose} footer={null} width={720}>
      {!isPremium ? (
        <div style={{ textAlign: 'center', padding: '30px 10px' }}>
          <div style={{ fontSize: 40 }}>🔒</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 10 }}>网盘为高级会员专享</div>
          <div style={{ color: '#8c8c8c', marginTop: 6, marginBottom: 16 }}>升级为高级会员，即可上传 PPT / Word / Excel / PDF / 图片 / 视频，每人 100M 空间。</div>
        </div>
      ) : (
        <>
          {/* 容量 */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
              <span>已用 {usedMB.toFixed(1)}M / {quotaMB.toFixed(0)}M</span>
              <span>{overview?.fileCount || 0} 个文件 · {folders.length} 个目录</span>
            </div>
            <Progress percent={pct} size="small" strokeColor={pct > 90 ? '#f5222d' : '#6366F1'} showInfo={false} />
          </div>

          {/* 目录行 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 12 }}>
            {folders.map((f) => (
              <Tag.CheckableTag
                key={f.id}
                checked={activeFolder === f.id}
                onChange={() => setActiveFolder(f.id)}
                style={{ borderRadius: 999, padding: '3px 10px', border: '1px solid #eee', fontSize: 13 }}
              >
                <FolderOutlined /> {f.name} ({f.file_count || 0})
              </Tag.CheckableTag>
            ))}
            <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => { setNewFolderName(''); setNewFolderOpen(true); }}>新建目录</Button>
            {activeFolder && (
              <Popconfirm title="删除该目录及其中所有文件？" onConfirm={() => deleteFolder(activeFolder)} okText="删除" cancelText="取消">
                <Button size="small" danger type="text" icon={<DeleteOutlined />}>删除当前目录</Button>
              </Popconfirm>
            )}
          </div>

          {/* 上传 + 文件列表 */}
          {activeFolder ? (
            <>
              <div style={{ marginBottom: 12 }}>
                <Button type="primary" icon={<CloudUploadOutlined />} loading={uploading} onClick={triggerUpload}>上传文件</Button>
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 10 }}>支持 PPT/Word/Excel/PDF/图片/视频等</Text>
                <input ref={inputRef} type="file" accept={ACCEPT} style={{ display: 'none' }} onChange={onFilePicked} />
              </div>
              {files.length === 0 ? (
                <Empty description="该目录暂无文件" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '48vh', overflow: 'auto' }}>
                  {files.map((f) => (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid #f0f1f4', borderRadius: 10 }}>
                      <span style={{ fontSize: 22 }}>{fileIcon(f.file_type)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.original_name}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>{fmtSize(f.size)} · {(f.file_type || '').toUpperCase()}</div>
                      </div>
                      <Button size="small" type="link" onClick={() => openFile(f)}>打开</Button>
                      <Popconfirm title="删除该文件？" onConfirm={() => deleteFile(f.id)} okText="删除" cancelText="取消">
                        <Button size="small" type="link" danger>删除</Button>
                      </Popconfirm>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Empty description="请先新建或选择一个目录" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </>
      )}

      {/* 新建目录弹窗 */}
      <Modal title="新建目录" open={newFolderOpen} onCancel={() => setNewFolderOpen(false)} onOk={createFolder} okText="创建">
        <Input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="请输入目录名，如：工作合同" maxLength={50} onPressEnter={createFolder} autoFocus />
      </Modal>
    </Modal>
  );
};

export default NetdiskModal;
